import React, { ReactElement, useContext } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import { timestampToDateTime } from '../../utils/parse'
import { RawActivityEvent, RawRunEvent } from '../../utils/type'
import { StaticDataTypeI18nKey } from '../chart-components'
import { StaticDataContext } from '../../context/StaticDataContext'

interface RunStatusProps {
    runEvent: RawRunEvent
    tubularEvent: RawActivityEvent
}

export const RunStatusOnActivity = ({ runEvent, tubularEvent }: RunStatusProps): ReactElement => {
    const { liveTheme } = useContext(StaticDataContext)

    return (
        <StatusArea liveTheme={liveTheme}>
            <StatusTitle>{i18n('Run Workstring Current Status')}</StatusTitle>
            <StatusInfoContainer>
                <EntitySection>
                    <EntityTitle>{i18n(StaticDataTypeI18nKey.RUN)}</EntityTitle>
                    <StatusInfoContent liveTheme={liveTheme}>
                        <StatusSection>
                            <StatusSectionTitle>{i18n('NUMBER')}</StatusSectionTitle>
                            <StatusSectionInfo>{runEvent?.number}</StatusSectionInfo>
                        </StatusSection>
                        {!runEvent.tubularsIntervalPerTime && <Divisor />}
                        {!runEvent?.tubularsIntervalPerTime && (
                            <StatusSection>
                                <StatusSectionTitle style={{ width: 40 }}>{i18n('Final MD')}</StatusSectionTitle>
                                <StatusSectionInfo>
                                    {`${runEvent?.finalMD?.value} ${runEvent?.finalMD?.uom?.name || ''}`}
                                </StatusSectionInfo>
                            </StatusSection>
                        )}
                    </StatusInfoContent>
                </EntitySection>
                <EntitySection>
                    <EntityTitle>TUBULAR</EntityTitle>
                    <StatusInfoContent liveTheme={liveTheme}>
                        <StatusSection
                            style={
                                runEvent.tubularsIntervalPerTime ? { paddingRight: '16px' } : { paddingRight: '10px' }
                            }
                        >
                            <StatusSectionTitle>{i18n('INTERVAL')}</StatusSectionTitle>
                            <StatusSectionInfo>
                                {i18n(runEvent?.tubularsIntervalPerTime ? 'TIME' : 'DEPTH')}
                            </StatusSectionInfo>
                        </StatusSection>
                        <Divisor />
                        <StatusSection>
                            <StatusSectionTitle>{i18n('PREVIOUS')}</StatusSectionTitle>
                            <StatusSectionInfo>
                                {runEvent?.tubularsIntervalPerTime
                                    ? `${timestampToDateTime(runEvent?.end).date} - ${
                                          timestampToDateTime(runEvent?.end).time
                                      }`
                                    : tubularEvent.startMD?.value
                                    ? `${tubularEvent.startMD?.value} ${tubularEvent.startMD?.uom?.name}`
                                    : 'NA'}
                            </StatusSectionInfo>
                        </StatusSection>
                    </StatusInfoContent>
                </EntitySection>
            </StatusInfoContainer>
        </StatusArea>
    )
}

const StatusSectionTitle = styled.div`
    font-style: normal;
    text-transform: uppercase;
    font-weight: 400;
    font-size: 10px;
    line-height: 13px;
    color: var(--gray-700);
`

const StatusSectionInfo = styled.div`
    font-style: normal;
    font-weight: 600;
    font-size: 12px;
    line-height: 15px;
    color: var(--gray-800);
`

const StatusArea = styled.div<{ liveTheme: string }>`
    display: flex;
    flex-direction: column;
    padding: 8px;
    border-radius: 3px;
    background: ${({ liveTheme }) => (liveTheme === 'dark' ? 'var(--gray-300)' : 'var(--gray-100)')};
`

const StatusInfoContainer = styled.div`
    display: flex;
    gap: 8px;
`

const EntitySection = styled.section`
    display: flex;
    flex-direction: column;
`

const EntityTitle = styled.span`
    margin-bottom: 4px;
    font-style: normal;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 10px;
    line-height: 13px;
    color: var(--gray-800);
`

const StatusInfoContent = styled.div<{ liveTheme?: string }>`
    display: flex;
    width: 100%;
    padding: 5px 8px;
    border-radius: 3px;

    background: ${({ liveTheme }) => (liveTheme === 'dark' ? 'var(--gray-200)' : 'white')};
`

const StatusSection = styled.div`
    display: flex;
    flex-direction: column;
`

const Divisor = styled.div`
    width: 1px;
    height: 100%;
    margin-left: 5px;
    margin-right: 8px;

    background-color: var(--gray-400);
`

const StatusTitle = styled.div`
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: var(--blue-500);
    padding-bottom: 8px;
`
